import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Sparkles, Heart, MessageCircle, Users, BookOpen, Sun } from "lucide-react";

const CONVO_PROMPTS = [
  "What's something you're proud of?",
  "Where do you feel most at peace?",
  "What makes you laugh until you cry?",
  "What's one lesson life has taught you?",
];

export default function Today() {
  const { user } = useAuth();
  const [entry, setEntry] = useState("");
  const [g, setG] = useState({ items: [], top_words: [], streak: 0 });
  const [intros, setIntros] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [gRes, dRes] = await Promise.all([
        api.get("/gratitude"),
        api.get("/discover/today"),
      ]);
      setG(gRes.data);
      setIntros(dRes.data.intros || []);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const addG = async (e) => {
    e.preventDefault();
    if (!entry.trim()) return;
    setBusy(true);
    try { await api.post("/gratitude", { entry }); setEntry(""); await load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const todayStr = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

  return (
    <div className="space-y-10">
      {/* Hero greeting */}
      <div className="reveal">
        <div className="text-xs uppercase tracking-widest text-rose">{todayStr}</div>
        <h1 className="font-display text-5xl md:text-6xl text-navy mt-2 leading-tight" data-testid="today-greeting">
          Good day, <span className="italic text-rose">{user?.name?.split(" ")[0] || "friend"}</span>.
        </h1>
        <p className="text-navy/70 mt-2 max-w-2xl">A gentle place to pause, be grateful, and meet three thoughtful people today.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Gratitude card */}
        <div className="lg:col-span-2 glass rounded-3xl p-8 relative overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-rose">Daily gratitude</div>
              <h2 className="font-display text-3xl text-navy mt-1">What are you grateful for today?</h2>
            </div>
            <div className="text-right">
              <div className="text-3xl font-display text-sage" data-testid="grat-streak">{g.streak || 0}</div>
              <div className="text-[10px] uppercase tracking-widest text-navy/60">day streak</div>
            </div>
          </div>
          <form onSubmit={addG} className="mt-4">
            <textarea data-testid="grat-input" value={entry} onChange={(e) => setEntry(e.target.value)} rows={3}
              className="w-full px-5 py-4 rounded-2xl bg-white/80 border border-navy/10"
              placeholder="This morning I noticed…" />
            <div className="flex justify-end mt-3">
              <button data-testid="grat-submit" type="submit" disabled={busy || !entry.trim()}
                className="btn-pill bg-rose text-white hover:bg-rose-600 disabled:opacity-50">
                {busy ? "Saving..." : "Add to journal"}
              </button>
            </div>
          </form>
          <div className="mt-6">
            <div className="text-xs uppercase tracking-widest text-navy/60 mb-3">Recent entries</div>
            <div className="space-y-2 max-h-56 overflow-auto pr-2">
              {(g.items || []).slice(0, 6).map((it) => (
                <div key={it.id} data-testid={`grat-item-${it.id}`} className="p-4 rounded-2xl bg-white/60 border border-white/70">
                  <div className="text-sm text-navy">{it.entry}</div>
                  <div className="text-[10px] text-navy/50 mt-1">{it.date}</div>
                </div>
              ))}
              {(g.items || []).length === 0 && <div className="text-sm text-navy/50">Your first entry starts today.</div>}
            </div>
          </div>
        </div>

        {/* Trends */}
        <div className="glass rounded-3xl p-8">
          <div className="text-xs uppercase tracking-widest text-rose">Gratitude trends</div>
          <h3 className="font-display text-2xl text-navy mt-1">Your kind themes</h3>
          <div className="mt-5 flex flex-wrap gap-2">
            {(g.top_words || []).map((w) => (
              <span key={w.word} className="px-3 py-1.5 rounded-full bg-sage/15 text-sage-500 text-sm capitalize"
                style={{ fontSize: `${Math.min(1.4, 0.85 + w.count * 0.08)}rem` }}>
                {w.word}
              </span>
            ))}
            {(g.top_words || []).length === 0 && <div className="text-sm text-navy/50">Add a few entries to see your themes.</div>}
          </div>
          <div className="mt-8 rounded-2xl border border-sage/30 bg-sage/10 p-5 text-sm text-navy/80 leading-relaxed">
            <Sun size={16} className="text-sage inline mr-1" />
            <span className="italic">"Gratitude turns what we have into enough."</span>
          </div>
        </div>
      </div>

      {/* Today's intros */}
      <div>
        <div className="flex items-end justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-rose">Your three for today</div>
            <h2 className="font-display text-3xl text-navy mt-1">People we thought you'd love to know</h2>
          </div>
          <Link to="/app/discover" data-testid="today-see-more" className="text-sm text-navy/70 hover:text-navy">Open Discover →</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-5 mt-6">
          {intros.length === 0 && (
            <div className="col-span-3 glass rounded-3xl p-8 text-center text-navy/60">
              Come back tomorrow — or invite friends to grow the community.
            </div>
          )}
          {intros.map((p) => (
            <IntroCard key={p.id} p={p} />
          ))}
        </div>
      </div>

      {/* Quick links */}
      <div className="grid md:grid-cols-4 gap-4">
        {[
          { to: "/app/messages", label: "Messages", icon: MessageCircle },
          { to: "/app/communities", label: "Communities", icon: Users },
          { to: "/app/learn", label: "Learning Centre", icon: BookOpen },
          { to: "/app/date-ideas", label: "Date ideas", icon: Heart },
        ].map((q) => (
          <Link key={q.to} to={q.to} data-testid={`quick-${q.label.toLowerCase().replace(/\s/g, "-")}`}
            className="glass rounded-2xl p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-500">
            <q.icon size={22} className="text-rose" />
            <div className="mt-3 font-medium text-navy">{q.label}</div>
          </Link>
        ))}
      </div>

      {err && <div className="text-sm text-rose-600" data-testid="today-error">{err}</div>}
    </div>
  );
}

function IntroCard({ p }) {
  const initials = (p.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");
  return (
    <Link to={`/app/user/${p.id}`} data-testid={`intro-card-${p.id}`}
      className="group glass rounded-3xl p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-500">
      <div className="flex items-center justify-between">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-peach to-rose flex items-center justify-center text-white font-display text-xl">
          {initials}
        </div>
        <div className="text-right">
          <div className="text-2xl font-display text-sage">{p.compatibility?.score}%</div>
          <div className="text-[10px] uppercase tracking-widest text-navy/60">match</div>
        </div>
      </div>
      <div className="mt-4 font-display text-2xl text-navy">{p.name}, {p.age}</div>
      <div className="text-sm text-navy/60">{p.city || "Somewhere lovely"}</div>
      {p.compatibility?.shared_values?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {p.compatibility.shared_values.slice(0, 3).map((v) => (
            <span key={v} className="text-[11px] px-2 py-1 rounded-full bg-rose/10 text-rose">{v}</span>
          ))}
        </div>
      )}
      {p.profile?.personal_motto && (
        <div className="mt-4 text-sm italic text-navy/70">"{p.profile.personal_motto}"</div>
      )}
      <div className="mt-4 text-xs text-navy/50">Photos revealed after mutual interest.</div>
    </Link>
  );
}
